import apiClient from "./api";

export interface ToolExecutionLog {
  id: string;
  companyId?: string;
  userId?: string;
  chatSessionId?: string;
  chatResponseId?: string;
  toolCallId?: string;
  widgetId?: string;
  serverId: string;
  toolName: string;
  endpoint: string;
  httpMethod: string;
  httpStatusCode: number;
  isSuccess: boolean;
  executedAt: string;
  durationMs?: number;
  requestLength: number;
  responseLength: number;
  /** validation | api_4xx | api_5xx | auth | timeout | none — undefined on rows written before the attempt tracking. */
  failureKind?: FailureKind;
  /** 1-based attempt number within the tool loop; 2 means the model retried after a failure. */
  attempt?: number;
  argumentsHash?: string;
}

export type FailureKind =
  | "validation"
  | "api_4xx"
  | "api_5xx"
  | "auth"
  | "timeout"
  | "none";

export const FAILURE_KINDS: FailureKind[] = [
  "validation",
  "api_4xx",
  "api_5xx",
  "auth",
  "timeout",
  "none",
];

/** One attempt the model made at the same tool within the same assistant turn. */
export interface ToolExecutionAttempt {
  id: string;
  attempt?: number;
  failureKind?: FailureKind;
  httpStatusCode: number;
  isSuccess: boolean;
  /** False when the attempt never reached the API (rejected before dispatch). */
  dispatched: boolean;
  argumentsHash?: string;
  requestBody?: string;
  responseBody?: string;
  executedAt: string;
  durationMs?: number;
  subAgentLabel?: string;
}

export interface ToolExecutionLogDetail {
  id: string;
  companyId?: string;
  userId?: string;
  chatSessionId?: string;
  chatResponseId?: string;
  toolCallId?: string;
  widgetId?: string;
  serverId: string;
  toolName: string;
  endpoint: string;
  httpMethod: string;
  httpStatusCode: number;
  isSuccess: boolean;
  requestBody?: string;
  responseBody?: string;
  executedAt: string;
  durationMs?: number;
  failureKind?: FailureKind;
  attempt?: number;
  argumentsHash?: string;
  subAgentLabel?: string;
}

export interface PaginatedResult<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}

export const toolExecutionLogsService = {
  getAllLogs: async (
    page: number = 1,
    pageSize: number = 20,
    search?: string,
    isSuccess?: boolean,
    failureKind?: FailureKind,
  ): Promise<PaginatedResult<ToolExecutionLog>> => {
    const params: any = { page, pageSize };
    if (search) params.search = search;
    if (isSuccess !== undefined) params.isSuccess = isSuccess;
    if (failureKind) params.failureKind = failureKind;

    const response = await apiClient.get(`/toolexecutionlogs`, { params });
    return response.data;
  },

  getLog: async (id: string): Promise<ToolExecutionLogDetail> => {
    const response = await apiClient.get(`/toolexecutionlogs/${id}`);
    return response.data;
  },

  /** Every attempt at the same tool in the same assistant turn as `id`, in attempt order. */
  getAttempts: async (id: string): Promise<ToolExecutionAttempt[]> => {
    const response = await apiClient.get(`/toolexecutionlogs/${id}/attempts`);
    return response.data;
  },
};
